import React, { Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ModalActions } from "src/redux/Modal/actions";
import { ModalSelectors } from "src/redux/Modal/selectors";

import styles from "./styles.module.scss";

const isFunction = (func) => {
    return func && func.constructor && func.call && func.apply;
};

const Modal = (props) => {
    const modalStack = useSelector(ModalSelectors.ModalStack);
    const currentModal =
        modalStack.length > 0 ? modalStack[modalStack.length - 1] : null;

    if (!currentModal) return null;

    return (
        <Fragment>
            <ModalContent {...currentModal} />
            {currentModal.overlay && (
                <ModalContent {...currentModal.overlay} overlay />
            )}
        </Fragment>
    );
};

export const ModalContent = ({ component, options = {}, overlay = false }) => {
    const dispatch = useDispatch();

    const getWrapperClasses = (options) => {
        let classes = [styles.modalWrapper];

        options.long && classes.push(styles.wrapperPadding);

        return classes.join(" ");
    };

    const getContentClasses = (options) => {
        let classes = [styles.modal];

        options.large && classes.push(styles.large);

        options.small && classes.push(styles.small);

        options.padding0 && classes.push(styles.padding0);

        options.wide && classes.push(styles.wide);

        options.rounded && classes.push(styles.rounded);

        return classes.join(" ");
    };

    const getContentStyles = (options) => {
        let style = {};

        if (options.width) style.width = options.width;
        if (options.height) style.height = options.height;

        return style;
    };

    const handleModalWrapperClick = (e) => {
        if (options.ignoreClickOutside) return;

        if (e.target.classList.contains(styles.modalWrapper)) {
            if (overlay) {
                dispatch(ModalActions.Update({ overlay: null }));
                return;
            }

            if (isFunction(options.onClose)) {
                options.onClose();
            }

            dispatch(ModalActions.Close());

            if (isFunction(options.afterClose)) {
                options.afterClose();
            }
        }
    };

    return (
        <div
            className={getWrapperClasses(options)}
            onClick={handleModalWrapperClick}
        >
            <div
                className={getContentClasses(options)}
                style={getContentStyles(options)}
            >
                <Header options={options} overlay={overlay} />
                <Body component={component} options={options} />
            </div>
        </div>
    );
};

const Header = ({ options = {}, overlay = false }) => {
    const dispatch = useDispatch();

    const onClose = () => {
        if (isFunction(options.onClose)) {
            options.onClose();
        }
        if (isFunction(options.onCancel)) {
            options.onCancel();
        }
        if (overlay) {
            dispatch(ModalActions.Update({ overlay: null }));
        } else {
            dispatch(ModalActions.Close());
        }
        if (isFunction(options.afterClose)) {
            options.afterClose();
        }
    };

    if (!options.title) {
        return (
            <span
                className={options.upload ? styles.uploadClose : styles.close}
                data-testid="close-modal"
                onClick={onClose}
            />
        );
    } else if (options.settings) {
        return (
            <div className={styles.settingTitleWrapper}>
                <span
                    className={[styles.settingsClose, styles.closeTitle].join(
                        " "
                    )}
                    data-testid="close-modal"
                    onClick={onClose}
                />
                <div>
                    <span className={styles.settingsTitle}>
                        {options.title}
                    </span>
                </div>
            </div>
        );
    } else {
        return (
            <div className={styles.header}>
                <span className={styles.title}>{options.title}</span>
                <span
                    className={[styles.close, styles.closeTitle].join(" ")}
                    data-testid="close-modal"
                    onClick={onClose}
                />
            </div>
        );
    }
};

const Body = ({ component, options = {} }) => {
    const getStyleClasses = (options) => {
        let classes = [styles.modalContent];
        if (options.settings) classes.push(styles.settingsModalContent);
        options.title && classes.push(styles.withTitle);

        return classes.join(" ");
    };

    return (
        <div className={getStyleClasses(options)}>
            {React.createElement(component, options.customProps)}
        </div>
    );
};

export default Modal;
